import { splitBrandAndName } from "../lib/productDisplay";

/**
 * Inline brand · product label.
 *
 *  CeraVe   |   Foaming Facial Cleanser
 *  ↑ small/muted    ↑ larger/primary
 *
 * Use this everywhere a product is shown so we never concatenate
 * brand into the product name (and never duplicate it).
 *
 * `size`  — "sm" | "md" | "lg"
 * `tone`  — "default" | "muted" — mostly for cards on darker chrome
 * `align` — "row" | "stack"     — stack puts brand above name
 */
export function BrandProductLabel({
  product,
  size = "md",
  tone = "default",
  align = "row",
  className = "",
  productClassName = "",
  brandClassName = "",
}) {
  const { brand, name } = splitBrandAndName(product);
  if (!brand && !name) return null;

  const SIZES = {
    sm: { brand: "text-[11px]", name: "text-sm" },
    md: { brand: "text-xs", name: "text-[15px] sm:text-base" },
    lg: { brand: "text-[12px] sm:text-sm", name: "text-lg sm:text-xl" },
  };
  const s = SIZES[size] || SIZES.md;
  const muted = tone === "muted" ? "text-cream-100/70" : "text-ink-mute";
  const primary = tone === "muted" ? "text-cream-50" : "text-ink";

  if (align === "stack" || !brand) {
    return (
      <span
        data-testid="brand-product-label"
        className={`inline-flex flex-col leading-tight min-w-0 ${className}`}
      >
        {brand && (
          <span
            className={`${s.brand} font-medium uppercase tracking-[0.14em] ${muted} ${brandClassName}`}
          >
            {brand}
          </span>
        )}
        <span
          className={`${s.name} font-semibold ${primary} truncate ${productClassName}`}
        >
          {name}
        </span>
      </span>
    );
  }

  return (
    <span
      data-testid="brand-product-label"
      className={`inline-flex items-baseline gap-2 min-w-0 ${className}`}
    >
      <span
        className={`${s.brand} font-medium uppercase tracking-[0.14em] ${muted} shrink-0 ${brandClassName}`}
      >
        {brand}
      </span>
      <span
        className={`${s.brand} ${muted} shrink-0 select-none`}
        aria-hidden="true"
      >
        |
      </span>
      <span
        className={`${s.name} font-semibold ${primary} truncate ${productClassName}`}
      >
        {name}
      </span>
    </span>
  );
}

export default BrandProductLabel;
